import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Play, ExternalLink } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: "all", label: "All" },
  { value: "experiences", label: "Experiences" },
  { value: "veteran_stories", label: "Veteran Stories" },
  { value: "events", label: "Events" },
];

export default function EpisodesPage() {
  const [episodes, setEpisodes] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = activeCategory !== "all" ? `?category=${activeCategory}` : "";
    axios.get(`${API}/episodes${params}`)
      .then(r => setEpisodes(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [activeCategory]);

  return (
    <div data-testid="episodes-page">
      {/* ── HERO ── */}
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-24 noise-overlay relative">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <p className="font-body text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-4">Content Hub</p>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-[5.5rem] uppercase tracking-tighter leading-[0.9] mb-6">
              Episodes &<br/>Content
            </h1>
            <p className="font-body text-base lg:text-lg text-zinc-400 max-w-xl leading-relaxed">
              Real stories. Raw experiences. Watch veteran-focused content that inspires and impacts.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── CATEGORY FILTER ── */}
      <section className="border-b border-white/5 bg-zinc-950 sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-6 py-4 overflow-x-auto">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.value}
                data-testid={`category-filter-${cat.value}`}
                onClick={() => setActiveCategory(cat.value)}
                className={`font-subheading text-xs uppercase tracking-[0.15em] whitespace-nowrap pb-2 border-b-2 transition-colors duration-300 ${
                  activeCategory === cat.value
                    ? "text-white border-white"
                    : "text-zinc-500 border-transparent hover:text-zinc-300"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── VIDEO GRID ── */}
      <section className="py-16 lg:py-24 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="border border-white/5 animate-pulse">
                  <div className="aspect-video bg-zinc-900" />
                  <div className="p-6 space-y-3">
                    <div className="h-3 bg-zinc-900 w-1/3" />
                    <div className="h-5 bg-zinc-900 w-3/4" />
                    <div className="h-4 bg-zinc-900 w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : episodes.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-body text-zinc-500">No episodes found in this category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {episodes.map((ep, i) => {
                const videoId = ep.youtube_url ? ep.youtube_url.split('v=')[1]?.split('&')[0] : null;
                return (
                  <motion.div
                    key={ep.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1, duration: 0.5 }}
                    className="group border border-white/10 card-hover overflow-hidden"
                    data-testid={`episode-card-${i}`}
                  >
                    <div className="relative overflow-hidden aspect-video">
                      {videoId ? (
                        <iframe
                          src={`https://www.youtube.com/embed/${videoId}`}
                          title={ep.title}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          loading="lazy"
                        />
                      ) : (
                        <img src={ep.thumbnail} alt={ep.title} className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="p-6">
                      <div className="mb-2">
                        <span className="font-body text-[10px] uppercase tracking-[0.2em] text-zinc-600">
                          {ep.category.replace('_', ' ')}
                        </span>
                      </div>
                      <h3 className="font-subheading text-lg uppercase tracking-normal mb-2 line-clamp-2">
                        {ep.title}
                      </h3>
                      <p className="font-body text-sm text-zinc-500 line-clamp-2 mb-4">{ep.description}</p>
                      {ep.youtube_url && (
                        <a
                          href={ep.youtube_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`episode-watch-${i}`}
                          className="inline-flex items-center gap-2 font-subheading text-xs uppercase tracking-widest text-zinc-400 hover:text-white transition-colors"
                        >
                          Watch on YouTube <ExternalLink size={12} />
                        </a>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ── YOUTUBE CTA ── */}
      <section className="py-16 lg:py-24 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-4">Subscribe</p>
          <h2 className="font-subheading text-3xl sm:text-4xl uppercase tracking-tight mb-4">
            Never Miss An Episode
          </h2>
          <p className="font-body text-base text-zinc-500 mb-8 max-w-lg mx-auto">
            Subscribe to our YouTube channel for the latest veteran experiences and stories.
          </p>
          <a
            href="https://youtube.com/@BadCompanyEntertainmentTV"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="youtube-subscribe-link"
            className="inline-flex items-center gap-2 font-subheading text-sm uppercase tracking-widest bg-white text-black px-8 py-3.5 hover:bg-zinc-200 transition-colors duration-300"
          >
            Subscribe on YouTube <ExternalLink size={14} />
          </a>
        </div>
      </section>
    </div>
  );
}
